import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { RegistrationFormComponent } from '../registration-form/registration-form.component';
import { RegisterForm } from '../../interfaces/forms/register-form.interface';
import { AppConfigService, FullPageMaskService, ILookup } from 'common';
import { RegistrationService } from '../../services/registration.service';
import { SignupLookups } from '../../interfaces/lookups-result.interface';
import { MatButtonModule } from '@angular/material/button';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { SenderIdsFormComponent } from '../sender-ids-form/sender-ids-form.component';
import { SenderIdsOpasFormComponent } from '../sender-ids-opas-form/sender-ids-opas-form.component';
import { CommonModule } from '@angular/common';
import { PublicApiService } from '../../services/public-api.service';
import { SignupDeclarationComponent } from '../signup-declaration/signup-declaration.component';
import { SignupFormService } from '../../services/sign-up-form.service';
import { ReactiveFormsModule } from '@angular/forms';
import { lastValueFrom, tap } from 'rxjs';
import { RecaptchaFormsModule, RecaptchaModule, RecaptchaComponent, ReCaptchaV3Service } from 'ng-recaptcha';
import { HttpErrorResponse } from '@angular/common/http';
import { SignUpFormResponse } from '../../interfaces/forms/sign-up-form-response.interface';

@Component({
  selector: 'si-signup',
  standalone: true,
  imports: [RegistrationFormComponent, MatButtonModule, RouterModule, MatStepperModule, SenderIdsFormComponent, SenderIdsOpasFormComponent, CommonModule, SignupDeclarationComponent, ReactiveFormsModule, RecaptchaModule, RecaptchaFormsModule],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent implements OnInit {
  @ViewChild('stepper') private stepper: MatStepper;
  @ViewChild('captchaRef') private captchaRef: RecaptchaComponent;

  signupFormService = inject(SignupFormService);
  maskService = inject(FullPageMaskService);
  registrationService = inject(RegistrationService);
  publicApiService = inject(PublicApiService);
  route = inject(ActivatedRoute);
  env = inject(AppConfigService);
  elementRef = inject(ElementRef);

  orgTypes: ILookup[] = [];
  countries: ILookup[] = [];
  accountTypes: ILookup[] = [];

  private selectedThirdParties: string[][] = [];

  public isSubmitted = false;
  public hasFile = false;
  public errorMessage = '';

  opas$ = this.publicApiService.getAllOpasList$();
  thirdParties$ = this.publicApiService.getAllThirdPartiesList$().pipe(
    tap((value) => this.signupFormService.thirdParties = value),
  );

  public get mainForm() {
    return this.signupFormService.formGroup;
  }

  public get signupDeclarationForm() {
    return this.signupFormService.signupDeclarationForm.formGroup;
  }

  public get recaptchaForm() {
    return this.signupFormService.recaptchaFormService.formGroup;
  }

  public get siteId() {
    return this.env.config.reCaptchaSiteId;
  }

  public get accountType() {
    return (this.mainForm.controls.steps.value[0] as RegisterForm).accountType;
  }

  public get steps() {
    return this.signupFormService.formGroup.controls.steps;
  }

  public get disablePickOpasStep() {
    return this.signupFormService.senderIdsFormService.formArray.value
      .filter(v => v.trim().length > 0).length === 0;
  }

  ngOnInit(): void {
    const { organisationTypes, countries, accountTypes } = this.route.snapshot.data.lookups as SignupLookups;
    this.orgTypes = organisationTypes;
    this.countries = countries;
    this.accountTypes = accountTypes;

    this.signupFormService.initFormGroup();
  }

  handleGoToPickOpas() {
    if (this.disablePickOpasStep) {
      this.signupFormService.senderIdsOpasFormService.reset();
    }
  }

  public handleThirdPartiesChanged(selectedThirdParties: string[][]) {
    this.selectedThirdParties = selectedThirdParties;
  }

  async handleSubmit() {
    this.signupDeclarationForm.markAsTouched();
    this.mainForm.markAllAsTouched();

    if (this.mainForm.invalid || this.signupDeclarationForm.invalid) {
      return;
    }

    const { data, file } = this.signupFormService.toRegisterFormWithSenderIdsRequest(this.selectedThirdParties);

    this.maskService.showLoading();
    this.hasFile = !!file;

    try {
      this.errorMessage = '';
      await lastValueFrom(this.registrationService.submit$(data, file));
      this.isSubmitted = true;
    } catch (response) {
      if (response instanceof HttpErrorResponse) {
        const data = (response.error) as SignUpFormResponse | null;
        const showErrorMessage = data && data.title || !Object.keys(data.validationErrors)[0].length

        if (showErrorMessage) {
          this.errorMessage = data.title ?? Object.values(data.validationErrors)[0]?.[0] ?? '';
        }
      }
    } finally {
      this.maskService.hideLoading();
      this.captchaRef.reset();
    }
  }

  public handleNextButtonClick() {
    const invalidFields = this.elementRef.nativeElement.querySelectorAll('mat-form-field.ng-invalid');
    const firstInvalidField = invalidFields[0];

    if (!firstInvalidField) return;

    firstInvalidField.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  handleCaptchaResolve(captchaKey?: string) {
    if (!captchaKey) return;
    this.recaptchaForm.get('recaptcha')?.setValue(captchaKey);
  }
}
